'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'lodash';
import { Button, IconButton } from '@ui/button';
import { Eyebrow } from '@web/atoms';
import { IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { CMSBlok } from '../../cms/CMSBlok';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import {
  onboardingBackArrowClicked,
  userViewsOnboardingInterstitial,
  userContinuesFromInterstitial,
} from '../events';
import {
  StyledInterstitial,
  BackgroundImageContainer,
  MobileImageWrapper,
  DesktopImageWrapper,
  ArrowContainer,
  ContentOverlay,
  Headline,
  Subheadline,
  Body,
  ButtonContainer,
} from './styles.js';

export const OnboardingInterstitial = ({
  goBack,
  onSubmit,
  eyebrowText,
  mobileImage,
  desktopImage,
  headline,
  subheadline,
  body,
  backgroundColor = 'neutral.darkest',
  fontColor,
  ctaButtonText = 'Continue',
  backButtonBackgroundColor = 'background.lightest',
  backButtonIconColor = 'hero.plum',
  previousQuestion = '',
}) => {
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();
  const viewPageTagFired = useRef(false);

  const backButtonIcon = useMemo(
    () => ({
      iconName: 'arrow-left',
      size: '24px',
      alt: 'back',
      id: 'prev_step_footer',
      color: backButtonIconColor,
    }),
    [backButtonIconColor],
  );

  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(userViewsOnboardingInterstitial(previousQuestion));
      viewPageTagFired.current = true;
    }
  }, [reporter, previousQuestion]);

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked(
        'onboarding_interstitial',
        cart?.customer || customer,
      ),
    );
    goBack();
  };

  const handleSubmit = event => {
    event.preventDefault();
    reporter.tag(userContinuesFromInterstitial(previousQuestion));
    onSubmit();
  };

  return (
    <StyledInterstitial
      data-testid="onboarding-interstitial"
      onSubmit={handleSubmit}
    >
      <BackgroundImageContainer
        aria-hidden="true"
        $backgroundColor={backgroundColor}
      >
        <MobileImageWrapper>
          <ResponsiveImage
            priority={true}
            fill
            objectFit="cover"
            sizes={IMAGE_SIZES.fullBleed}
            {...omit(mobileImage, ['width', 'height'])}
          />
        </MobileImageWrapper>
        <DesktopImageWrapper>
          <ResponsiveImage
            priority={true}
            fill
            objectFit="cover"
            sizes={IMAGE_SIZES.fullBleed}
            {...omit(desktopImage, ['width', 'height'])}
          />
        </DesktopImageWrapper>
      </BackgroundImageContainer>
      <ArrowContainer
        aria-live="polite"
        $backButtonBackgroundColor={backButtonBackgroundColor}
      >
        <IconButton
          aria-label="go to previous step"
          data-testid="onboarding-interstitial-back"
          icon={backButtonIcon}
          onClick={goBackWithReporter}
          type="button"
          variant="secondary"
        />
      </ArrowContainer>
      <ContentOverlay data-testid="onboarding-interstitial-content">
        {eyebrowText && (
          <Eyebrow marginBottom={0} textAlign="center" fontColor={fontColor}>
            {eyebrowText}
          </Eyebrow>
        )}
        {headline && <Headline $fontColor={fontColor}>{headline}</Headline>}
        {subheadline && (
          <Subheadline $fontColor={fontColor}>{subheadline}</Subheadline>
        )}
        {Boolean(body?.length) && (
          <Body>
            {body.map(content => (
              <CMSBlok key={content._uid} content={content} />
            ))}
          </Body>
        )}
        <ButtonContainer>
          <Button
            type="submit"
            aria-label={ctaButtonText}
            data-testid="onboarding-interstitial-continue"
            variant="lilac"
            fullWidth
          >
            {ctaButtonText}
          </Button>
        </ButtonContainer>
      </ContentOverlay>
    </StyledInterstitial>
  );
};
OnboardingInterstitial.displayName = 'OnboardingInterstitial';
OnboardingInterstitial.propTypes = {
  mobileImage: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  desktopImage: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  headline: PropTypes.string,
  subheadline: PropTypes.node,
  body: PropTypes.arrayOf(PropTypes.object),
  eyebrowText: PropTypes.string,
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  ctaButtonText: PropTypes.string,
  backButtonBackgroundColor: PropTypes.string,
  backButtonIconColor: PropTypes.string,
  goBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  previousQuestion: PropTypes.string,
};
